import { ResponseEntity } from "domain/entity/Common/ResponseEntity";
import { MasterDataType } from "domain/entity/MasterData/MasterDataEntity";
import { EMPTY_PORT_ENTITY, PortEntity } from "domain/entity/Port/PortEntity";
import { EMPTY_PORT_SEARCH_CRITERIA, PortSearchCriteria } from "domain/entity/Port/PortSearchCriteria";
import { MasterDataRepository } from "domain/repository/MasterData/MasterDataRepo";
import { PortRepository } from "domain/repository/Port/PortRepo";
import { createPortValidationSchema } from "presentation/constant/Port/PortValidationSchema";
import { Validation } from "presentation/constant/Validation";
import { DropdownProps } from "presentation/model/DropdownProps";
import { PortModel } from "presentation/model/Port/PortModel";
import { Dispatch, SetStateAction } from "react";
import { IFieldValue } from "veronica-ui-component/dist/component/core";
import BaseViewModel from "../BaseViewModel";

interface PortVMProps extends BaseViewModel {
    dispatch: [
        Dispatch<SetStateAction<PortModel>> | ((value: SetStateAction<PortModel>) => void),
    ],
    portRepo: PortRepository,
    masterDataRepo: MasterDataRepository,
}

export const PortVM = ({ dispatch, portRepo, masterDataRepo }: PortVMProps) => {
    const [portDispatch] = dispatch;

    const loadDropdownOption = async () => {

        await masterDataRepo.getMasterDataByKey(MasterDataType.COUNTRY).then(
            async countryEntities => {
                await masterDataRepo.getMasterDataByKey(MasterDataType.PROVINCE).then(
                    provinceEntities => {

                        let countryCodeDropdownOption: DropdownProps[] = [];
                        let provinceDropdownOption: { [key: string]: DropdownProps[] } = {};

                        countryEntities?.forEach(country => {
                            const countryExisted = countryCodeDropdownOption?.find(v =>
                                country.value === country.code);
                            if (!countryExisted) {
                                countryCodeDropdownOption.push({
                                    dropdownLabel: country.code,
                                    tagLabel: country.code,
                                    value: country.code
                                })
                            }
                            provinceEntities?.forEach(province => {
                                if ((country.code === province.refCode) && province.code) {
                                    if (!provinceDropdownOption[country.code]) {
                                        provinceDropdownOption[country.code] = [];
                                    }
                                    provinceDropdownOption[country.code].push({
                                        dropdownLabel: province.code,
                                        tagLabel: province.code,
                                        value: province.code
                                    });
                                }
                            })
                        })

                        portDispatch(prevState => ({
                            ...prevState,
                            dynamicOptions: {
                                ...prevState.dynamicOptions,
                                countryCodeDropdownOptions: countryCodeDropdownOption,
                                provinceCodeDropdownOptions: provinceDropdownOption,
                            }
                        }))
                    }
                )
            }
        )

        await masterDataRepo.getMasterDataByKey(MasterDataType.GEO_IND).then(
            geoIndEntities => {
                const geographicalIndDropdownOption = geoIndEntities?.map((geoInd) => ({
                    dropdownLabel: geoInd.code,
                    tagLabel: geoInd.code,
                    value: geoInd.code,
                })) ?? []

                portDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        geographicalIndDropdownOptions: geographicalIndDropdownOption,
                    }
                }))
            }
        )

    }

    const searchPortList = async (searchCriteria: PortSearchCriteria) => {
        portDispatch(prevState => {
            return {
                ...prevState,
                currentRow: { ...EMPTY_PORT_ENTITY },
                currentEditRow: { ...EMPTY_PORT_ENTITY },
                isShowEditPanel: false,
                isAdd: false,
                isEdit: false,
                selectedRows: [],
                // portList: [],
            }
        })
        await portRepo.searchPortList(searchCriteria).then(data => {
            if (data) {
                portDispatch(prevState => {
                    return {
                        ...prevState,
                        portList: data,
                    }
                })
            } else {
                portDispatch(prevState => {
                    return {
                        ...prevState,
                        portList: [],
                    }
                })
            }
        })
    }

    const onDelete = async (rows: PortEntity[]) => {

        return await portRepo.onDelete(rows);
    }

    const updateSelectedRows = async (updatedRows: PortEntity[]) => {

        portDispatch(prevState => {
            return {
                ...prevState,
                selectedRows: updatedRows,
                forceRefresh: !prevState.forceRefresh
            }
        })
    }

    const onHeaderFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (fieldKey === 'portCode') {
            val = val.toString().toUpperCase();
        }
        portDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: {
                    ...prevState.currentEditRow,
                    [fieldKey]: val,
                },
                masterState: {
                    ...prevState.masterState,
                    allFormState: {
                        ...prevState.masterState.allFormState,
                        [fieldKey]: ''
                    },
                }
            }
        })
    }
    const onSearchFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;

        portDispatch(prevState => {
            return {
                ...prevState,
                searchCriteria: {
                    ...prevState.searchCriteria,
                    [fieldKey]: val,
                },
            }
        })
    }

    const onRowDoubleClick = async (entity: PortEntity) => {
        portDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: entity,
                currentRow: entity,
                isShowEditPanel: true,
            }
        })
    }

    const onCloseEidtPanel = async () => {
        portDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: { ...EMPTY_PORT_ENTITY },
                isShowEditPanel: false,
                isAdd: false,
                isEdit: false,
                selectedRows: [],
                masterState: {
                    ...prevState.masterState,
                    allFormState: {}
                }
            }
        })
    }

    const onSave = async (currentRow: PortEntity) => {

        const valResult = await Validation(createPortValidationSchema).ValidateFormOnly(currentRow);
        let validatedResult: { [x: string]: string } = {};
        if (valResult) {
            validatedResult = { ...valResult };

            portDispatch(prevState => {
                return {
                    ...prevState,
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...validatedResult
                        },
                    }
                }
            });

            const res: ResponseEntity = {
                code: "",
                success: false,
                msg: null,
                data: null
            }

            return res;
        }
        return portRepo.onSave(currentRow);
    }

    const onSaveClicked = () => {
        portDispatch(prevState => {
            return {
                ...prevState,
                masterState: {
                    ...prevState.masterState,
                    isSaveClicked: true,
                    allFormState: {},
                }
            }
        })
    }

    const onAddClick = async (operatingUnit?: string) => {
        portDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: operatingUnit==='OICT' ? { 
                    ...EMPTY_PORT_ENTITY,
                    countryCode: prevState.dynamicOptions?.countryCodeDropdownOptions?.sort((a, b) => a?.dropdownLabel!.localeCompare(b?.dropdownLabel!))?.[0]?.value || null,
                    geogInd: prevState.dynamicOptions?.geographicalIndDropdownOptions?.sort((a, b) => a?.dropdownLabel!.localeCompare(b?.dropdownLabel!))?.[0]?.value || null
                 }
                 : { ...EMPTY_PORT_ENTITY },
                isAdd: true,
                isShowEditPanel: true,
            }
        })
    }

    const onCheckboxChange = (checked: boolean, fieldName: string) => {
        portDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: {
                    ...prevState.currentEditRow,
                    [fieldName]: checked ? "Y" : "N",
                },
            }
        })
    }

    const onResetClick = async (isAdd: boolean) => {
        portDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: isAdd ? { ...EMPTY_PORT_ENTITY } : prevState.currentRow,
            }
        })
    }
    const onEditClick = async () => {
        portDispatch(prevState => {
            return {
                ...prevState,
                isEdit: true,
            }
        })
    }
    const onSearchCriteriaResetClick = async () => {
        portDispatch(prevState => {
            return {
                ...prevState,
                searchCriteria: { ...EMPTY_PORT_SEARCH_CRITERIA },
            }
        })
    }
    const onSearchClick = async () => {
        portDispatch(prevState => {
            return {
                ...prevState,
                isShowSearchPanel: !prevState.isShowSearchPanel,
            }
        })
    }

    const onShowLoading = () => {
        portDispatch(prevState => {
            return {
                ...prevState,
                isLoading: true,
            }
        })
    }

    const onHideLoading = () => {
        portDispatch(prevState => {
            return {
                ...prevState,
                isLoading: false,
            }
        })
    }

    const setIsSearchLoading = (isSearchLoading: boolean) => {
        portDispatch(prevState => {
            return {
                ...prevState,
                isSearchLoading: isSearchLoading,
            }
        })
    }

    return {
        onHideLoading: onHideLoading,
        onShowLoading: onShowLoading,
        onSearchClick: onSearchClick,
        onSearchCriteriaResetClick: onSearchCriteriaResetClick,
        onSearchFieldChange: onSearchFieldChange,
        onEditClick: onEditClick,
        onResetClick: onResetClick,
        onCheckboxChange: onCheckboxChange,
        onAddClick: onAddClick,
        onSave: onSave,
        onCloseEidtPanel: onCloseEidtPanel,
        onRowDoubleClick: onRowDoubleClick,
        onHeaderFieldChange: onHeaderFieldChange,
        updateSelectedRows: updateSelectedRows,
        onDelete: onDelete,
        loadDropdownOption: loadDropdownOption,
        searchPortList: searchPortList,
        onSaveClicked: onSaveClicked,
        setIsSearchLoading: setIsSearchLoading,
    }
}